import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { WrenchIcon, CheckCircleIcon, SendIcon, Loader2Icon, MapPinIcon, UsersIcon, SparklesIcon, ClockIcon, BadgeEuroIcon, SearchIcon, FileTextIcon, CalendarIcon, HardHatIcon, KeyIcon, CheckIcon, MessageCircleIcon } from 'lucide-react';
import { ContactSection } from '../components/ContactSection';
import { useLanguage } from '../contexts/LanguageContext';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
type FormData = {
  nombre: string;
  email: string;
  telefono: string;
  ubicacion: string;
  descripcionEspacio: string;
  tipoReforma: string;
  presupuesto: string;
  detalles: string;
};
export function RenovationsPage() {
  const {
    t
  } = useLanguage();
  const highlights = [{
    icon: UsersIcon,
    title: t('renovation.h1.title'),
    description: t('renovation.h1.desc')
  }, {
    icon: SparklesIcon,
    title: t('renovation.h2.title'),
    description: t('renovation.h2.desc')
  }, {
    icon: ClockIcon,
    title: t('renovation.h3.title'),
    description: t('renovation.h3.desc')
  }, {
    icon: BadgeEuroIcon,
    title: t('renovation.h4.title'),
    description: t('renovation.h4.desc')
  }];
  const processSteps = [{
    icon: SearchIcon,
    title: t('page.renovation.process.step1.title'),
    desc: t('page.renovation.process.step1.desc')
  }, {
    icon: FileTextIcon,
    title: t('page.renovation.process.step2.title'),
    desc: t('page.renovation.process.step2.desc')
  }, {
    icon: CalendarIcon,
    title: t('page.renovation.process.step3.title'),
    desc: t('page.renovation.process.step3.desc')
  }, {
    icon: HardHatIcon,
    title: t('page.renovation.process.step4.title'),
    desc: t('page.renovation.process.step4.desc')
  }, {
    icon: KeyIcon,
    title: t('page.renovation.process.step5.title'),
    desc: t('page.renovation.process.step5.desc')
  }];
  const whyChooseUs = [t('page.renovation.why.item1'), t('page.renovation.why.item2'), t('page.renovation.why.item3'), t('page.renovation.why.item4'), t('page.renovation.why.item5'), t('page.renovation.why.item6')];
  const [formData, setFormData] = useState<FormData>({
    nombre: '',
    email: '',
    telefono: '',
    ubicacion: '',
    descripcionEspacio: '',
    tipoReforma: '',
    presupuesto: '',
    detalles: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsSubmitting(false);
    setIsSubmitted(true);
  };
  return <div className="w-full min-h-screen bg-white" data-id="element-400">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] w-full bg-black overflow-hidden pt-20" data-id="element-401">
        <div className="absolute inset-0 z-0" data-id="element-402">
          <img src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&q=80" alt="Renovation" className="w-full h-full object-cover opacity-50" data-id="element-403" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" data-id="element-404" />
        </div>

        <div className="relative z-10 flex flex-col items-center justify-center min-h-[70vh] px-6 py-24" data-id="element-405">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="max-w-4xl mx-auto text-center" data-id="element-406">
            <motion.div className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 backdrop-blur-sm rounded-full text-white font-medium border border-white/10 mb-8" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} data-id="element-407">
              <MapPinIcon className="w-4 h-4" data-id="element-408" />
              {t('hero.badge')}
            </motion.div>

            <motion.h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 drop-shadow-xl" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} data-id="element-409">
              <span className="text-white" data-id="element-410">
                {t('page.renovation.hero.title1')}
              </span>
              <br data-id="element-411" />
              <span className="bg-gradient-to-r from-white via-neutral-200 to-neutral-400 bg-clip-text text-transparent" data-id="element-412">
                {t('page.renovation.hero.title2')}
              </span>
            </motion.h1>

            <motion.p className="text-lg md:text-xl text-neutral-300 max-w-2xl mx-auto leading-relaxed font-medium" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} data-id="element-413">
              {t('page.renovation.hero.subtitle')}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Highlights Section */}
      <section className="w-full bg-white py-28 px-6" data-id="element-414">
        <div className="max-w-7xl mx-auto" data-id="element-415">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="mb-16" data-id="element-416">
            <div className="flex items-center gap-3 mb-4" data-id="element-417">
              <div className="w-14 h-14 bg-black rounded-2xl flex items-center justify-center shadow-lg" data-id="element-418">
                <WrenchIcon className="w-7 h-7 text-white" data-id="element-419" />
              </div>
              <span className="text-sm font-bold tracking-widest text-neutral-500 uppercase" data-id="element-420">
                {t('renovation.label')}
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6" data-id="element-421">
              {t('renovation.title')}
            </h2>
            <p className="text-lg text-neutral-600 max-w-3xl leading-relaxed" data-id="element-422">
              {t('renovation.subtitle')}
            </p>
          </motion.div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-423">
            {highlights.map((item, index) => <motion.div key={index} variants={itemVariants} className="p-6 bg-neutral-50 rounded-2xl group border border-neutral-100 shadow-sm hover:shadow-xl transition-all duration-300" whileHover={{
            y: -4
          }} data-id="element-424">
                <motion.div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center mb-4 shadow-md" whileHover={{
              scale: 1.1,
              rotate: 5
            }} data-id="element-425">
                  <item.icon className="w-6 h-6 text-white" data-id="element-426" />
                </motion.div>
                <h3 className="text-lg font-bold text-black mb-2" data-id="element-427">
                  {item.title}
                </h3>
                <p className="text-sm text-neutral-600 leading-relaxed" data-id="element-428">
                  {item.description}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="w-full bg-neutral-50 py-28 px-6" data-id="element-429">
        <div className="max-w-7xl mx-auto" data-id="element-430">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="grid md:grid-cols-3 gap-4" data-id="element-431">
            {['https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=600&q=80', 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=600&q=80', 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&q=80', 'https://images.unsplash.com/photo-1600573472550-8090b5e0745e?w=600&q=80', 'https://images.unsplash.com/photo-1600566752355-35792bedcfea?w=600&q=80', 'https://images.unsplash.com/photo-1600210492493-0946911123ea?w=600&q=80'].map((img, index) => <motion.div key={index} className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-lg" whileHover={{
            scale: 1.02
          }} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1 * index
          }} data-id="element-432">
                <img src={img} alt={`Reforma integral ${index + 1}`} className="w-full h-full object-cover saturate-[0.8] hover:saturate-[1.1] transition-all duration-500" data-id="element-433" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 hover:opacity-100 transition-opacity" data-id="element-434" />
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section className="w-full bg-white py-28 px-6" data-id="element-435">
        <div className="max-w-7xl mx-auto" data-id="element-436">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="text-center mb-16" data-id="element-437">
            <span className="text-sm font-bold tracking-widest text-neutral-500 uppercase mb-4 block" data-id="element-438">
              {t('page.renovation.process.label')}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-black" data-id="element-439">
              {t('page.renovation.process.title')}
            </h2>
          </motion.div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="grid md:grid-cols-5 gap-6" data-id="element-440">
            {processSteps.map((step, index) => <motion.div key={index} variants={itemVariants} className="relative text-center" data-id="element-441">
                <motion.div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg" whileHover={{
              scale: 1.1,
              rotate: 5
            }} data-id="element-442">
                  <step.icon className="w-7 h-7 text-white" data-id="element-443" />
                </motion.div>
                <h3 className="font-bold text-black mb-2" data-id="element-444">{step.title}</h3>
                <p className="text-sm text-neutral-600" data-id="element-445">{step.desc}</p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="w-full bg-neutral-50 py-28 px-6" data-id="element-446">
        <div className="max-w-4xl mx-auto" data-id="element-447">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="text-center mb-12" data-id="element-448">
            <span className="text-sm font-bold tracking-widest text-neutral-500 uppercase mb-4 block" data-id="element-449">
              {t('page.renovation.why.label')}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-black" data-id="element-450">
              {t('page.renovation.why.title')}
            </h2>
          </motion.div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="grid sm:grid-cols-2 gap-4" data-id="element-451">
            {whyChooseUs.map((item, index) => <motion.div key={index} variants={itemVariants} className="flex items-center gap-4 p-5 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow" whileHover={{
            x: 4
          }} data-id="element-452">
                <div className="w-10 h-10 bg-black rounded-full flex items-center justify-center flex-shrink-0 shadow-md" data-id="element-453">
                  <CheckIcon className="w-5 h-5 text-white" data-id="element-454" />
                </div>
                <span className="font-medium text-black" data-id="element-455">{item}</span>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Form Section - REPLACED WITH WHATSAPP CTA */}
      <section className="w-full bg-gradient-to-br from-neutral-100 to-neutral-200 py-28 px-6" data-id="element-456">
        <div className="max-w-3xl mx-auto text-center" data-id="element-457">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} data-id="element-458">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-6" data-id="element-459">
              ¿Pensando en reformar?
            </h2>
            <p className="text-neutral-600 mb-12 text-lg max-w-2xl mx-auto" data-id="element-460">
              Hablemos de tu proyecto. Escríbenos por WhatsApp y te asesoraremos
              sobre las mejores opciones para tu reforma.
            </p>

            <motion.a href="https://wa.me/34631012592?text=Hola,%20me%20gustaría%20solicitar%20información%20sobre%20reformas%20integrales" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 px-10 py-5 bg-[#25D366] text-white font-bold text-xl rounded-full transition-all duration-300 hover:bg-[#20bd5a] hover:shadow-lg hover:shadow-[#25D366]/20" whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} data-id="element-461">
              <MessageCircleIcon className="w-8 h-8" data-id="element-462" />
              Contactar por WhatsApp
            </motion.a>

            <div className="mt-8 text-neutral-500" data-id="element-463">
              <p className="text-sm" data-id="element-464">
                O llámanos al{' '}
                <a href="tel:+34631012592" className="text-black font-medium hover:underline" data-id="element-465">
                  +34 631 01 25 92
                </a>
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <ContactSection data-id="element-466" />
    </div>;
}